/**
 * Local days for the media library's day tabs. A day is a local thing: the day a clock changes is
 * 23 or 25 hours long, so its bounds come from the zone's own offsets rather than arithmetic on
 * UTC. Pure, so it is unit tested.
 */

export type AssetDay = { day: string; count: number };

const PARTS = new Map<string, Intl.DateTimeFormat>();
function parts(timeZone: string): Intl.DateTimeFormat {
  const cached = PARTS.get(timeZone);
  if (cached) return cached;
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  PARTS.set(timeZone, fmt);
  return fmt;
}

/** How far the zone is from UTC at this instant, in milliseconds (east is positive). */
function offsetMsAt(instantMs: number, timeZone: string): number {
  const f: Record<string, string> = {};
  for (const p of parts(timeZone).formatToParts(new Date(instantMs))) f[p.type] = p.value;
  const asUtc = Date.UTC(
    Number(f["year"]),
    Number(f["month"]) - 1,
    Number(f["day"]),
    Number(f["hour"]),
    Number(f["minute"]),
    Number(f["second"]),
  );
  return asUtc - Math.floor(instantMs / 1000) * 1000;
}

/**
 * The instant at which `YYYY-MM-DD HH:00` begins in `timeZone`. Two passes: the first offset is
 * read at the wrong instant by definition, the second at the candidate, which settles every case
 * except a wall-clock time the zone skipped — there the earlier offset stands and the instant lands
 * just after the jump, which is what a reader means by "the start of that day".
 */
function localStart(day: string, timeZone: string): number {
  const guess = Date.parse(`${day}T00:00:00Z`);
  const first = offsetMsAt(guess, timeZone);
  const candidate = guess - first;
  const second = offsetMsAt(candidate, timeZone);
  return second === first ? candidate : guess - second;
}

const nextDay = (day: string) => {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

/** The UTC instants bounding one local day, as the assets list wants them: [from, to). */
export function dayRange(day: string, timeZone: string): { from: string; to: string } {
  return {
    from: new Date(localStart(day, timeZone)).toISOString(),
    to: new Date(localStart(nextDay(day), timeZone)).toISOString(),
  };
}

/**
 * Which local day an instant falls in, as `YYYY-MM-DD`. Read from the zone's own formatter rather
 * than by dividing by 86 400 000: that arithmetic is wrong for every reader away from UTC, and
 * wrong for everyone twice a year.
 */
export function localDayOf(instantMs: number, timeZone: string): string {
  const f: Record<string, string> = {};
  for (const p of parts(timeZone).formatToParts(new Date(instantMs))) f[p.type] = p.value;
  return `${f["year"]}-${f["month"]}-${f["day"]}`;
}

/** "Mon 12" — the weekday and day number of that calendar date, the same for every reader. */
export function dayTabLabel(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  const at = new Date(Date.UTC(y!, m! - 1, d!));
  const weekday = new Intl.DateTimeFormat(undefined, { timeZone: "UTC", weekday: "short" }).format(
    at,
  );
  return `${weekday} ${at.getUTCDate()}`;
}

/** The tab to land on: the most recent day that holds something. */
export function latestDay(days: AssetDay[]): string | null {
  return days.reduce<string | null>(
    (best, d) => (best === null || d.day > best ? d.day : best),
    null,
  );
}

/** The assets query for one tab: a day's window, or the recordings that have no time at all. */
export function dayQuery(day: string | null, timeZone: string, cursor?: string): string {
  const q = new URLSearchParams({ limit: "200" });
  if (day === null) q.set("undated", "1");
  else {
    const { from, to } = dayRange(day, timeZone);
    q.set("from", from);
    q.set("to", to);
  }
  if (cursor) q.set("cursor", cursor);
  return q.toString();
}

/** A hard stop, so a bad range can never spin this loop: no trip is three years of tabs. */
const MAX_DAYS = 1000;

/**
 * Every local day touched by a span, in order, as `YYYY-MM-DD`. Walked through the zone's own day
 * bounds rather than by adding 24 hours, so a day that gains or loses an hour still counts once.
 */
export function daysBetween(fromMs: number, toMs: number, timeZone: string): string[] {
  if (!(toMs >= fromMs)) return [];
  const last = localDayOf(toMs, timeZone);
  const out: string[] = [];
  let day = localDayOf(fromMs, timeZone);
  for (let i = 0; i < MAX_DAYS; i++) {
    out.push(day);
    if (day >= last) return out;
    // The next day is wherever this one ends, which the zone decides.
    day = localDayOf(Date.parse(dayRange(day, timeZone).to), timeZone);
  }
  return out;
}

export type TripDay = {
  /** `YYYY-MM-DD`, local. */
  day: string;
  /** The instants bounding it: [from, to), the zone's own midnights. */
  from: number;
  to: number;
  /** The first and last recording it holds, or null on a day that holds none. */
  first: number | null;
  last: number | null;
  count: number;
};

/**
 * The trip's days, each with its bounds and what was recorded in it.
 *
 * One definition of "the trip's days" for every surface that offers them as tabs: the phone
 * map's day strip and the editor's timeline ask the same question, and a day that is Tuesday on
 * one screen and Wednesday on the other would make the two disagree about where a recording
 * belongs. Days with nothing in them are kept — an editor has to see that Wednesday was travelled
 * and never filmed, and a strip that skips it says instead that the trip skipped it.
 *
 * `from`/`to` come from the zone's own offsets via [dayRange], so the day a clock changes is 23 or
 * 25 hours long here too. Pure, so it is unit tested.
 */
export function tripDays(
  times: number[],
  fromMs: number,
  toMs: number,
  timeZone: string,
): TripDay[] {
  // Bucketed by the day each instant actually falls in, rather than compared against every day's
  // bounds: one formatter pass per recording instead of one per recording per day.
  const byDay = new Map<string, number[]>();
  for (const t of times) {
    if (!Number.isFinite(t) || t < fromMs || t > toMs) continue;
    const day = localDayOf(t, timeZone);
    const held = byDay.get(day);
    if (held) held.push(t);
    else byDay.set(day, [t]);
  }
  return daysBetween(fromMs, toMs, timeZone).map((day) => {
    const { from, to } = dayRange(day, timeZone);
    const held = (byDay.get(day) ?? []).sort((a, b) => a - b);
    return {
      day,
      from: Date.parse(from),
      to: Date.parse(to),
      first: held[0] ?? null,
      last: held[held.length - 1] ?? null,
      count: held.length,
    };
  });
}
